import { z } from "zod";

// Phase 7 typed configSchema for the `ranked-tier` template (D-05..D-09).
// Replaces the Phase 01.1 z.unknown() placeholder in TEMPLATE_CATALOG so that
// Phase 6's validateRecipe Pass 2A enforces the per-widget config shape end-to-end.
// (Plan 05 swaps this schema into TEMPLATE_CATALOG; this file ships pure schema.)
//
// D-07 lean composition (NOT nesting): RankedTierConfig carries `optionSetWidgetId`
// referencing an option-set widget instance in the same recipe. Per-option tier
// assignment lives in the option-set's options[i].tierId (a Phase-7 additive
// extension to Phase 6's _optionEntrySchema — the SOLE mutation Phase 7 makes to
// the Phase 6 option-set schema). The ranked-tier widget itself owns no renderer
// state; tier overlay is a render-time decoration consuming option-set state via
// useOtherWidgetState(optionSetWidgetId) (Plan 09, not this plan).
//
// RF-2 (research finding) widens D-05's `color: string` to a `{ bg, border, fg }`
// trio per pancake reference HTML lines 51-62. A single hex doesn't capture the // grep-allow: pancake
// pancake palette's bg/border/fg parity (different hues, not just lightness // grep-allow: pancake
// shifts) — recipe authors deserve direct control over the trio.
//
// D-06: soft cap N <= 12 enforced at the schema level via z.array(...).max(12).
// Past 12, visual tier distinction collapses anyway; cap lives here (schema's
// responsibility), not at the renderer.
//
// D-08 + PITFALLS §N3 structural mitigation: schema CARRIES tier data only; the
// renderer (Plan 09) emits data-tier-rank attributes + recipe-declared CSS
// custom-properties (--tier-{id}-bg/-border/-fg). NO named .tier-best / // grep-allow: tier-best
// .tier-good / .tier-ok / .tier-bad classes anywhere — Plan 01 grep gate // grep-allow: tier-good, tier-ok, tier-bad
// enforces in apps/web/src/**.
//
// Color strings are length-bounded (50 chars — enough for any valid hex / rgb()
// / hsl() / oklch() / lab() / lch() form) AND positive-list regex-constrained
// to one of: `#hex` (3-8 digits), `rgb()`/`rgba()`, `hsl()`/`hsla()`, `oklch()`,
// `oklab()`, `lab()`, `lch()`, `color()`, or a CSS named color identifier
// (alphabetical run). The positive list admits every modern CSS color format
// while excluding the smuggling vectors `expression(`, `url(`, `javascript:`,
// `attr(`, `var(`, `<`/`>`, `;`, CSS-comment delimiters. The renderer (Plan 09)
// applies these via inline style custom-properties (UI-SPEC line 805 — the SOLE
// permitted inline style in Phase 7); without this positive-list bound the
// schema admits "anything 1-50 chars" which trades defense-in-depth for
// "allow any CSS color format." Mission Critical defense-in-depth: helmet CSP
// `style-src 'self' 'unsafe-inline'` (Phase 6 contingency) covers the
// inline-style attack surface as the structural / browser-layer mitigation;
// this schema-layer regex is the Pass 2A input-validator mitigation.
//
// Cross-recipe widget-id resolution (does optionSetWidgetId actually point to an
// existing option-set widget?) is NOT enforced here — that requires a recipe-root
// walk. Phase 6's validateRecipe Pass 2A loops per-widget; a Pass 2B for
// cross-references is future work (out of Plan 03 scope).
//
// isolatedDeclarations + verbatimModuleSyntax + exactOptionalPropertyTypes
// (Phase 01.1 D-10/D-15 + Phase 6 D-09): export the interfaces explicitly and
// annotate the const with z.ZodType<RankedTierConfig> so tsc can emit
// declarations without reading across module boundaries (no TS9013).

export interface TierColorTrio {
  readonly bg: string;
  readonly border: string;
  readonly fg: string;
}

export interface RankedTier {
  id: string;
  label: string;
  rank: number;
  colors: TierColorTrio;
}

export interface RankedTierConfig {
  optionSetWidgetId: string;
  tiers: ReadonlyArray<RankedTier>;
}

// CSS color format positive-list. Admits the common modern CSS color formats
// (hex, rgb/rgba, hsl/hsla, oklch, oklab, lab, lch, color, named identifier)
// and rejects smuggling vectors at the schema layer. Per-format inner-syntax
// validation is the browser CSS parser's job (defense-in-depth) — this regex
// is the structural input-validator gate.
//
//   `#[0-9a-fA-F]{3,8}`  — hex (3/4/6/8 digit forms, with/without alpha)
//   `rgb[a]?\([^<>;]*\)` — rgb()/rgba() with no `<`, `>`, or `;` inside
//   `hsl[a]?\([^<>;]*\)` — hsl()/hsla()
//   `oklch\([^<>;]*\)`   — modern wide-gamut formats
//   `oklab\([^<>;]*\)`
//   `lab\([^<>;]*\)`
//   `lch\([^<>;]*\)`
//   `color\([^<>;]*\)`   — color() function for arbitrary color spaces
//   `[a-zA-Z]+`          — CSS named color identifier (one alphabetical run)
//
// The `[^<>;]*` inner bound rejects `<script`, `>`, and `;` inside function
// arguments — these are the canonical CSS-injection delimiters. `expression(`,
// `url(`, `javascript:`, `attr(`, `var(` are rejected because they do not
// match the start of any allowed alternative.
const CSS_COLOR_RE =
  /^(#[0-9a-fA-F]{3,8}|rgba?\([^<>;]*\)|hsla?\([^<>;]*\)|oklch\([^<>;]*\)|oklab\([^<>;]*\)|lab\([^<>;]*\)|lch\([^<>;]*\)|color\([^<>;]*\)|[a-zA-Z]+)$/;

// Color trio strictObject — each component a non-empty length-bounded string
// passing the CSS_COLOR_RE positive list. See header note for the rationale
// behind picking a positive list over a permissive "any 1-50 chars" bound.
const _tierColorsSchema = z.strictObject({
  bg: z.string().min(1).max(50).regex(CSS_COLOR_RE, "CSS color value required"),
  border: z.string().min(1).max(50).regex(CSS_COLOR_RE, "CSS color value required"),
  fg: z.string().min(1).max(50).regex(CSS_COLOR_RE, "CSS color value required"),
});

// Tier strictObject — kebab-case id regex matches the option-set._optionEntrySchema
// pattern so option.tierId references stay in the same id space. `rank` is a
// strictly positive integer bounded at 12 (D-06 soft cap N <= 12). The bound on
// the rank VALUE (not just the tiers.length) is load-bearing: renderer Branch B
// (apps/web/src/widgets/ranked-tier/renderer.tsx) emits classNames of the form
// `tier-rank-${rank}-bg / -border / -fg`, and globals.css declares exactly 36
// such classes (12 per axis × 3 axes). A rank above 12 would produce classNames
// with no matching CSS rules — silently breaking SC#2 / PITFALLS §N3 tier
// coloring. The superRefine on the root catches duplicate rank values across
// tiers (path: ["tiers", i, "rank"]).
const _tierSchema = z.strictObject({
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  label: z.string().min(1).max(200),
  rank: z.number().int().positive().max(12),
  colors: _tierColorsSchema,
});

// Top-level schema: tiers.min(1) — a ranked-tier with zero tiers is meaningless;
// tiers.max(12) — D-06 soft cap. superRefine emits per-index issues for duplicate
// ids and duplicate ranks, mirroring summary-checklist's nested-arrays
// multi-issue pattern (one ctx.addIssue per offence; both id and rank checks
// share the same loop so a tier with both kinds of duplication emits both
// issues simultaneously).
export const RankedTierConfigSchema: z.ZodType<RankedTierConfig> = z
  .strictObject({
    optionSetWidgetId: z.string().min(1),
    tiers: z.array(_tierSchema).min(1).max(12),
  })
  .superRefine((cfg, ctx) => {
    const seenIds = new Set<string>();
    const seenRanks = new Set<number>();
    for (const [i, t] of cfg.tiers.entries()) {
      if (seenIds.has(t.id)) {
        ctx.addIssue({
          code: "custom",
          path: ["tiers", i, "id"],
          message: `duplicate tier id "${t.id}"`,
        });
      }
      seenIds.add(t.id);
      if (seenRanks.has(t.rank)) {
        ctx.addIssue({
          code: "custom",
          path: ["tiers", i, "rank"],
          message: `duplicate tier rank ${t.rank}`,
        });
      }
      seenRanks.add(t.rank);
    }
  });
